import React, { useEffect, useState, createContext, useContext } from 'react';
import { signIn, signUp, signOut, getCurrentUser, getSession } from '../services/supabaseAuthService';
import { isSupabaseConfigured } from '../services/supabaseClient';
import { createProfile } from '../services/profileService';
interface User {
  id: string;
  email: string;
  type: 'brand' | 'organizer' | 'admin' | 'super_admin';
  name: string;
}
interface AuthContextType {
  user: User | null;
  currentUser: User | null; // Alias for user to maintain compatibility
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, role: 'brand' | 'organizer', name: string) => Promise<User>;
  logout: () => void;
  loading: boolean;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    // Check for existing session on load
    const checkSession = async () => {
      try {
        const session = await getSession();
        if (session) {
          const user = await getCurrentUser();
          if (user && user.user_metadata) {
            setUser({
              id: user.id,
              email: user.email || '',
              type: user.user_metadata.type || 'brand',
              name: user.user_metadata.name || ''
            });
          }
        }
      } catch (error) {
        console.error('Session check error:', error);
      } finally {
        setLoading(false);
      }
    };
    checkSession();
  }, []);
  const register = async (email: string, password: string, role: 'brand' | 'organizer', name: string): Promise<User> => {
    // Check if Supabase is configured before attempting registration
    if (!isSupabaseConfigured()) {
      throw new Error('Database not configured. Please contact the administrator to set up Supabase credentials.');
    }
    try {
      // Sign up the user with metadata
      const {
        user
      } = await signUp(email, password, {
        type: role,
        name: name
      });
      if (!user) {
        throw new Error('Registration failed');
      }
      const newUser: User = {
        id: user.id,
        email: user.email || '',
        type: role,
        name: name
      };
      setUser(newUser);
      return newUser;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };
  const login = async (email: string, password: string) => {
    // Check if Supabase is configured before attempting login
    if (!isSupabaseConfigured()) {
      throw new Error('Database not configured. Please contact the administrator to set up Supabase credentials.');
    }
    try {
      const {
        user
      } = await signIn(email, password);
      if (user && user.user_metadata) {
        const loggedInUser: User = {
          id: user.id,
          email: user.email || '',
          type: user.user_metadata.type || 'brand',
          name: user.user_metadata.name || ''
        };
        setUser(loggedInUser);
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };
  const logout = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };
  return <AuthContext.Provider value={{
    user,
    currentUser: user,
    login,
    register,
    logout,
    loading
  }} data-id="element-1260">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}